/**
 * ResizeObserver Error Handler
 *
 * This module provides comprehensive error handling for ResizeObserver loop limit exceeded errors.
 * These errors occur when ResizeObserver callbacks trigger DOM changes that cause more resize events,
 * creating an infinite loop. The browser throws this error to prevent infinite loops.
 */

// Global flag to prevent multiple error handlers
let isErrorHandlerInstalled = false

/**
 * Installs global error handlers for ResizeObserver loop limit exceeded errors
 */
export function installResizeObserverErrorHandlers(): void {
  if (isErrorHandlerInstalled) return

  // Override console.error to catch and suppress ResizeObserver loop limit exceeded errors
  const originalConsoleError = console.error
  console.error = (...args) => {
    const errorMessage = args[0]
    if (typeof errorMessage === 'string' && errorMessage.includes('ResizeObserver loop limit exceeded')) {
      console.warn('ResizeObserver loop limit exceeded - this is a known browser limitation and can be safely ignored')
      return
    }
    return originalConsoleError.apply(console, args)
  }

  // Add global error event listener
  window.addEventListener('error', (event) => {
    if (event.error && event.error.message && event.error.message.includes('ResizeObserver loop limit exceeded')) {
      event.preventDefault()
      console.warn('ResizeObserver loop limit exceeded - this is a known browser limitation and can be safely ignored')
      return false
    }
    return true
  })

  // Add unhandled rejection handler for ResizeObserver errors
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop limit exceeded')) {
      event.preventDefault()
      console.warn('ResizeObserver loop limit exceeded - this is a known browser limitation and can be safely ignored')
      return false
    }
    return true
  })

  isErrorHandlerInstalled = true
}

/**
 * Creates a safe ResizeObserver with error handling and debouncing
 */
export function createSafeResizeObserver(callback: (entries: ResizeObserverEntry[]) => void): ResizeObserver | null {
  let timeoutId: number | null = null
  let isProcessing = false
  let lastCallTime = 0
  const DEBOUNCE_DELAY = 16 // ~60fps
  const MAX_CALLS_PER_SECOND = 60

  const debouncedCallback = (entries: ResizeObserverEntry[]) => {
    const now = Date.now()

    // Prevent excessive calls
    if (now - lastCallTime < 1000 / MAX_CALLS_PER_SECOND) {
      return
    }

    if (isProcessing) {
      return
    }

    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }

    timeoutId = window.setTimeout(() => {
      try {
        isProcessing = true
        lastCallTime = Date.now()
        callback(entries)
      } catch (error) {
        console.warn('ResizeObserver callback error:', error)
      } finally {
        isProcessing = false
        timeoutId = null
      }
    }, DEBOUNCE_DELAY)
  }

  try {
    return new ResizeObserver(debouncedCallback)
  } catch (error) {
    console.warn('Failed to create ResizeObserver:', error)
    return null
  }
}

/**
 * Wraps a ResizeObserver callback with error handling
 */
export function wrapResizeObserverCallback(callback: (entries: ResizeObserverEntry[]) => void) {
  return (entries: ResizeObserverEntry[]) => {
    try {
      callback(entries)
    } catch (error) {
      console.warn('ResizeObserver callback error:', error)
    }
  }
}

// Auto-install error handlers when this module is imported
if (typeof window !== 'undefined') {
  installResizeObserverErrorHandlers()
}
